<template>
  <pl-dialog :size="'lg'">
    <header class="border-b px-7 py-4 flex items-center text-xl font-semibold">
      <span v-if="selected_tab === 'personal_info'">Personal Info </span>
      <span v-else-if="selected_tab === 'subjects'">Subjects Taught</span>
      <span v-else>Grades</span>
    </header>
    <body class="px-7 pt-4 mb-16 flex-grow h-full overflow-auto">
      <!-- personal info -->
      <div v-if="selected_tab === 'personal_info'">
        <div
          class="bg-gray-100 px-5 rounded py-3 mt-2 flex items-center text-gray-500"
        >
          <i class="fa-solid fa-circle-info text-lg mr-4"></i>
          <div>
            <p class="font-semibold">
              Hi {{ getProfile.name }}. Welcome to Parlay!
            </p>
            <p class="mt-1">
              Let’s start your profile, share a little more about yourself and
              select subjects and grades you teach.
            </p>
          </div>
        </div>
        <div class="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-0">
          <div class="sm:mr-2.5">
            <p>Avatar</p>
            <div class="flex mt-1 items-center">
              <img
                class="h-24 w-24 object-cover bg-center rounded-full"
                :src="getProfile.avatar_url"
              />
              <div class="ml-5">
                <button
                  class="px-4 py-2 bg-input rounded cursor-pointer text-gray-500"
                  @click.prevent="openPopupChooseAvatar"
                >
                  Change Avatar
                </button>
                <div class="mt-2 text-gray-500 text-xs">
                  Allowed types: jpeg, png, gif
                </div>
                <div class="text-gray-500 text-xs">Size limit: 2MB</div>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-16 sm:ml-2.5">
            <div>
              Universe Profile URL
              <i
                class="fas fa-question-circle text-gray-300 hover:text-primary universe"
                v-my-tooltip="{
                  content: tooltip_content,
                  placement: 'top',
                  classes: ['universe-profile-url'],
                }"
                aria-hidden="true"
              />
            </div>
            <div class="flex">
              <span
                class="flex items-center rounded rounded-r-none outline-none px-2 text-sm bg-gray-200 text-gray-500 cursor-default"
              >
                universe.parlayideas.com/profiles/
              </span>
              <input
                v-model="profile_url"
                class="bg-input text-sm text-gray-500 flex-grow outline-none pl-1 pr-4 py-2 w-full rounded rounded-l-none placeholder-gray-300"
                :placeholder="'Profile URL'"
                type="text"
                @input="checkProfileUrlExistence(profile_url)"
              />
            </div>
            <div class="h-5 text-red-500 text-xs">
              {{ profileUrlTaken ? 'Profile URL already taken.' : '' }}
              {{
                profileInvalidChars
                  ? 'Only lowercase letters, numbers and dashes are allowed.'
                  : ''
              }}
              {{
                profileUrlCannotBeEmpty
                  ? 'Profile URL cannot be empty and must be between 3 and 99 characters'
                  : ''
              }}
            </div>
          </div>
          <div class="sm:mr-2.5">
            <p>Name</p>
            <input
              v-model="name"
              class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded placeholder-gray-300"
              :placeholder="'Name'"
              type="text"
              @input="checkName(name)"
            />
            <div class="h-5 text-red-500 text-xs mt-1">
              {{ nameIsRequired ? 'Your name is required' : '' }}
              {{
                nameValidation
                  ? 'Your name must be between 2 and 99 characters'
                  : ''
              }}
            </div>
          </div>
          <div class="sm:ml-2.5">
            <p>
              Preferred Name
              <span class="text-xs">(what your students will see)</span>
            </p>
            <input
              v-model="preferred_name"
              class="bg-input text-sm text-gray-500 flex-grow outline-none px-4 py-2 w-full rounded placeholder-gray-300"
              :placeholder="'Preferred Name'"
              type="text"
              @input="checkPreferredName(preferred_name)"
            />
            <div class="h-5 text-red-500 italic text-xs">
              {{
                preferredNameValidation
                  ? 'Your preferred name must be between 2 and 99 characters'
                  : ''
              }}
            </div>
          </div>
        </div>
        <div>
          <p class="mb-1">About</p>
          <textarea
            v-model="description"
            class="bg-input focus-within:border-primary border border-solid border-input text-sm text-gray-500 flex-grow outline-none px-4 py-3 w-full rounded placeholder-gray-300 h-28 resize-none"
            :placeholder="'Tell us a little bit about yourself'"
            type="text"
          />
        </div>
      </div>
      <!-- subjects -->
      <div v-else-if="selected_tab === 'subjects'">
        <div
          class="bg-gray-100 px-5 flex items-center rounded py-3.5 mt-2 text-gray-500"
        >
          <i class="fa-solid fa-circle-info text-lg mr-4"></i>
          <div class="text-sm">
            <p>
              Select the <span class="font-semibold">subjects/topics</span> you
              teach.
            </p>
            <p class="mt-1">
              We will use this information to make relevant RoundTable and
              instructional content recommendations.
            </p>
          </div>
        </div>
        <div class="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div v-for="subject in subjects" :key="subject.id">
            <button
              class="px-4 py-2 w-full flex items-center rounded border border-solid cursor-pointer text-sm"
              :class="
                selectedSubjects.find((s) => s === subject.id)
                  ? ['bg-primary border-primary text-white']
                  : ['bg-white border-gray-400 text-gray-500']
              "
              @click="selectUnselectSubjects(subject.id)"
            >
              {{ subject.name }}
            </button>
          </div>
        </div>
      </div>
      <!-- grades -->
      <div v-else>
        <div
          class="bg-gray-100 flex items-center px-5 py-3.5 mt-2 text-gray-500"
        >
          <i class="fa-solid fa-circle-info text-lg mr-4"></i>
          <div>
            <p>
              Select the <span class="font-semibold">grades</span> you teach.
            </p>
            <p class="mt-1">
              We will use this <span class="font-semibold">information</span> to
              make relevant RoundTable and instructional
              <span class="font-semibold">content recommendations.</span>
            </p>
          </div>
        </div>
        <div class="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div v-for="gr in gradeLevels" :key="gr.id">
            <button
              class="px-4 py-2 w-full flex items-center rounded border border-solid cursor-pointer text-sm"
              :class="
                selectedGradeLevels.find((g) => g === gr.id)
                  ? ['bg-primary border-primary text-white']
                  : ['bg-white border-gray-400 text-gray-500']
              "
              @click="selectUnselectGrades(gr.id)"
            >
              {{ gr.grade }}
            </button>
          </div>
        </div>
      </div>
    </body>
    <footer class="px-7 py-4 absolute inset-x-0 bottom-0">
      <div class="flex items-center justify-between">
        <div class="text-xs">
          <i
            :class="
              selected_tab === 'personal_info'
                ? ['text-gray-500']
                : ['text-gray-300']
            "
            class="fas fa-circle cursor-pointer"
            @click="selected_tab = 'personal_info'"
          />
          <i
            :class="
              selected_tab === 'subjects'
                ? ['text-gray-500']
                : ['text-gray-300']
            "
            class="fas fa-circle cursor-pointer"
            @click="selected_tab = 'subjects'"
          />
          <i
            :class="
              selected_tab === 'grades' ? ['text-gray-500'] : ['text-gray-300']
            "
            class="fas fa-circle cursor-pointer"
            @click="selected_tab = 'grades'"
          />
        </div>
        <div class="flex items-center text-sm">
          <button
            v-if="selected_tab === 'subjects' || selected_tab === 'grades'"
            class="px-8 py-2 bg-gray-100 rounded cursor-pointer text-gray-500 font-semibold mr-2.5"
            @click="goToBackTab"
          >
            Back
          </button>
          <button
            v-if="
              selected_tab === 'personal_info' || selected_tab === 'subjects'
            "
            :disabled="
              profileUrlTaken ||
              profileInvalidChars ||
              profileUrlCannotBeEmpty ||
              (selected_tab === 'subjects' && !selectedSubjects.length)
            "
            class="px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
            @click="goToNextTab"
          >
            Next
          </button>
          <button
            v-else
            :disabled="disabledButton"
            class="px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
            @click="createFirstCourse_updateProfile()"
          >
            <div v-if="isCreating" class="loader"></div>
            <div v-else>Done</div>
          </button>
        </div>
      </div>
    </footer>
    <choose-avatar
      @close="show_choose_avatar_modal = false"
      v-if="show_choose_avatar_modal"
    />
  </pl-dialog>
</template>
<script>
  import { mapGetters } from 'vuex';
  import store from 'store';
  import updateUser from 'services/account/updateUser';
  import { encodeImageUrl } from 'utils/encodeImage';
  import ChooseAvatar from 'components/modals/chooseAvatar.vue';
  import profileServices from 'services/profile/profileServices';
  import { makeRequest } from 'utils/requestLogger';
  import { get } from 'lodash';

  export default {
    data() {
      return {
        name: null,
        preferred_name: null,
        description: '',
        profile_url: null,
        subjects: [],
        initialSubjects: [],
        selectedSubjects: [],
        gradeLevels: [],
        initialGradeLevels: [],
        selectedGradeLevels: [],
        selected_tab: 'personal_info',
        show_choose_avatar_modal: false,
        nameIsRequired: false,
        nameValidation: false,
        preferredNameValidation: false,
        profileUrlCannotBeEmpty: false,
        profileUrlTaken: false,
        profileInvalidChars: false,
        isCreating: false,
        tooltip_content:
          '<div class="pt-2.5 pb-5 text-xs"><p class="px-5 pb-2.5"> This is the unique URL for your teacher portfolio in the Parlay Universe. </p><p class="px-5 pt-2.5">When you create discussion topics, you can add the prompt to your portfolio to share with your colleagues and other teachers around the world.</p></div> ',
      };
    },
    components: {
      ChooseAvatar,
    },
    computed: {
      ...mapGetters(['getProfile', 'getCourses']),
      disabledButton() {
        return (
          this.nameIsRequired ||
          this.nameValidation ||
          this.preferredNameValidation ||
          this.profileUrlTaken ||
          this.profileInvalidChars ||
          this.isCreating === true ||
          this.profileUrlCannotBeEmpty ||
          !this.selectedSubjects.length ||
          !this.selectedGradeLevels.length
        );
      },
      courses_I_teach() {
        return this.getCourses.filter((course) => {
          if (course.is_sample !== true && course.is_archived === false) {
            const role = get(course, 'users[0].subscription.role');
            return ['author', 'co-teacher'].includes(role);
          }
        });
      },
    },
    methods: {
      createProfileUrl() {
        profileServices
          .createProfile(this.getProfile.id)
          .then(({ data }) => {
            this.profile_url = data.profile_url;
          })
          .catch((error) => {
            console.error(error);
          });
      },
      loadGradeLevels() {
        this.$store.dispatch('getAllGrades').then((results) => {
          this.gradeLevels = results;
        });
      },
      loadSubjects() {
        this.$store.dispatch('getSubjects').then((results) => {
          this.subjects = results;
        });
      },
      encodeImageURI(image_url) {
        return encodeImageUrl(image_url);
      },
      openPopupChooseAvatar() {
        this.show_choose_avatar_modal = true;
      },
      goToBackTab() {
        if (this.selected_tab === 'grades') {
          this.selected_tab = 'subjects';
        } else {
          this.selected_tab = 'personal_info';
        }
      },
      goToNextTab() {
        if (this.selected_tab === 'personal_info') {
          this.selected_tab = 'subjects';
        } else {
          this.selected_tab = 'grades';
        }
      },
      selectUnselectSubjects(id) {
        const index = this.selectedSubjects.findIndex((s) => s === id);
        if (index > -1) {
          this.selectedSubjects.splice(index, 1);
        } else {
          this.selectedSubjects.push(id);
        }
      },
      selectUnselectGrades(id) {
        const index = this.selectedGradeLevels.findIndex((s) => s === id);
        if (index > -1) {
          this.selectedGradeLevels.splice(index, 1);
        } else {
          this.selectedGradeLevels.push(id);
        }
      },
      checkProfileUrlExistence(profileName) {
        this.profileUrlCannotBeEmpty = !profileName;
        if (this.profileUrlCannotBeEmpty) {
          this.profileUrlTaken = false;
          this.profileInvalidChars = false;
        }
        if (
          !this.profileUrlCannotBeEmpty &&
          (profileName.length < 3 || profileName.length > 99)
        ) {
          this.profileUrlCannotBeEmpty = true;
          this.profileUrlTaken = false;
          this.profileInvalidChars = false;
          return;
        }

        // We only allow lowercase letters, numbers and the dash symbol.
        const validUrlChars = 'abcdefghijklmnopqrstuvwxyz0123456789-';
        this.profileInvalidChars = false;
        for (var i = 0; i < profileName.length; i++) {
          if (validUrlChars.indexOf(profileName.charAt(i)) === -1) {
            this.profileInvalidChars = true;
            this.profileUrlTaken = false;
            this.profileUrlCannotBeEmpty = false;
            return;
          }
        }
        if (!this.profileInvalidChars && profileName) {
          makeRequest('get', `/api/profiles/url?search=${profileName}`)
            .then((res) => {
              if (res.data) {
                this.profileUrlTaken = false;
              } else if (
                !res.data &&
                profileName.toLowerCase() !==
                  this.getProfile.profile_url.toLowerCase()
              ) {
                this.profileUrlTaken = true;
                this.profileInvalidChars = false;
                this.profileUrlCannotBeEmpty = false;
                return;
              }
            })
            .catch((err) => {
              console.error(err);
              this.profileUrlTaken = true;
            });
        }
      },
      checkName(name) {
        if (!name || name.trim() === '') {
          this.nameIsRequired = true;
          return null;
        } else if ((name && name.length < 2) || name.length > 99) {
          this.nameValidation = true;
          return null;
        }
        this.nameIsRequired = false;
        this.nameValidation = false;
      },
      checkPreferredName(new_preferred_name) {
        if (
          new_preferred_name !== null &&
          new_preferred_name !== '' &&
          (new_preferred_name.length < 2 || new_preferred_name.length > 99)
        ) {
          this.preferredNameValidation = true;
          return null;
        }
        this.preferredNameValidation = false;
      },
      createFirstCourse_updateProfile: async function () {
        this.isCreating = true;
        const course_payload = {
          title: 'My First Course',
          sections: ['001'],
          banner_url: '/static/banners/Classical.png',
        };
        const user_payload = {};
        const role = this.getProfile.role;
        const a = this.selectedSubjects.filter(
          (s) => !this.initialSubjects.includes(s)
        );
        const b = this.selectedGradeLevels.filter(
          (g) => !this.initialGradeLevels.includes(g)
        );
        const c = this.initialSubjects.filter(
          (s) => !this.selectedSubjects.includes(s)
        );
        const d = this.initialGradeLevels.filter(
          (g) => !this.selectedGradeLevels.includes(g)
        );
        const diff_subjects = a.concat(c);
        const diff_grades = b.concat(d);
        if (role === 'teacher') {
          if (!this.courses_I_teach.length)
            await store.dispatch('createCourse', course_payload);
          user_payload.name = this.name.trim();
          user_payload.profile_url = this.profile_url.toLowerCase();
          if (this.preferred_name && this.preferred_name.trim() !== '') {
            user_payload.preferred_name = this.preferred_name.trim();
          }
          if (this.description && this.description.trim() !== '') {
            user_payload.description = this.description.trim();
          }
          user_payload.subjects = this.selectedSubjects.map((ss) =>
            this.subjects.find((s) => s.id === ss)
          );
          user_payload.grade_levels = this.selectedGradeLevels.map((gg) =>
            this.gradeLevels.find((g) => g.id === gg)
          );
          user_payload.subjects_changed = diff_subjects.length ? true : false;
          user_payload.grade_levels_changed = diff_grades.length ? true : false;
          return updateUser(this.getProfile.id, user_payload)
            .then((res) => {
              this.$store.dispatch('setAccount', res.data);
              this.$emit('close');
              this.$emit('openWelcomeModal');
              this.isCreating = false;
            })
            .catch((e) => {
              console.error(e);
              this.$emit('close');
            });
        }
      },
    },
    async mounted() {
      this.$router.replace({ query: { on_boarding: true } });
      if (this.getProfile.id && this.getProfile.profile_url === null) {
        this.createProfileUrl();
      } else {
        this.profile_url = this.getProfile.profile_url;
        await this.checkProfileUrlExistence(this.profile_url);
      }
      this.name = this.getProfile.name;
      this.preferred_name = this.getProfile.preferred_name;
      this.description = this.getProfile.description;

      this.initialGradeLevels = this.getProfile.grade_levels.map((g) => g.id);
      this.initialSubjects = this.getProfile.subjects.map((s) => s.id);
      this.selectedGradeLevels = this.getProfile.grade_levels.map((g) => g.id);
      this.selectedSubjects = this.getProfile.subjects.map((s) => s.id);
      this.loadSubjects();
      this.loadGradeLevels();
    },
    beforeDestroy() {
      const { courseID, roundtableID, liveDiscussionID } = this.$route.params;
      if (courseID) {
        this.$store.dispatch('enterCourse', courseID);
        if (roundtableID) {
          this.$store.dispatch('fetchRoundtable', roundtableID);
        }
        if (liveDiscussionID) {
          this.$store.dispatch('fetchLiveDiscussion', liveDiscussionID);
        }
      }
    },
  };
</script>
<style lang="scss">
  .tooltip {
    &.universe-profile-url {
      width: 400px;
    }
  }
</style>



// WEBPACK FOOTER //
// welcomeModal.vue?78b18f11